
import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import DashboardCards from '@/components/dashboard/DashboardCards';
import SubscriptionChart from '@/components/dashboard/SubscriptionChart';
import { Button } from '@/components/ui/button';
import { Plus, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SubscriptionTable from '@/components/subscriptions/SubscriptionTable';
import { useQuery } from '@tanstack/react-query';
import { getSubscriptionsMinimal } from '@/services/subscription';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/hooks/useLanguage';
import { Subscription } from '@/lib/data';

const Index = () => {
  const { translate } = useLanguage();
  const { data, isLoading, error } = useQuery({
    queryKey: ['subscriptions-minimal'],
    queryFn: getSubscriptionsMinimal
  });

  // Extract subscriptions from the response
  const subscriptions = data?.subscriptions || [];
  
  // Log any errors to help with debugging
  React.useEffect(() => {
    if (error) {
      console.error('Error loading subscriptions on dashboard:', error);
    } else {
      console.log(`Dashboard loaded ${subscriptions.length} subscriptions with MRC: ${data?.totalMRC || 0}`);
      // Log some sample data for debugging
      if (subscriptions.length > 0) {
        console.log('Sample subscription:', subscriptions[0]);
        if (subscriptions[0].services && subscriptions[0].services.length > 0) {
          console.log('Sample service:', subscriptions[0].services[0]);
        }
      }
    }
  }, [error, subscriptions, data]);

  // Get recent subscriptions (last 5)
  const recentSubscriptions = [...subscriptions]
    .sort((a, b) => {
      const dateA = new Date(a.registered_at || '');
      const dateB = new Date(b.registered_at || '');
      return dateB.getTime() - dateA.getTime();
    })
    .slice(0, 5);

  // Convert to Subscription type for SubscriptionTable
  const typedRecentSubscriptions = recentSubscriptions.map(sub => ({
    ...sub,
    subscription_id: sub.subscription_id,
    subscription_number: sub.subscription_number || '',
    customer_id: sub.customer?.id || '', 
    customer: {
      id: sub.customer?.id || '',
      customer_name: sub.customer?.customer_name || 'Unknown',
      customer_number: sub.customer?.customer_number || '',
      email: '',
      phone: '',
      customer_type: 'private',
      postal_street: '',
      postal_zip_code: '',
      postal_city: '',
      created_at: '',
      updated_at: ''
    },
    status: sub.status,
    services: sub.services || [],
    registered_at: sub.registered_at,
    installation_street: '',
    installation_zip_code: sub.installation_zip_code || '',
    installation_city: sub.installation_city || '',
    housing_type: sub.housing_type || 'house',
    created_at: '',
    created_by: ''
  })) as Subscription[];

  return (
    <MainLayout>
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{translate('dashboard.title')}</h1>
          <p className="text-muted-foreground">{translate('dashboard.subtitle')}</p>
        </div>
        <Link to="/subscriptions/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            {translate('dashboard.newSubscription')}
          </Button>
        </Link>
      </div>

      <DashboardCards />

      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">{translate('dashboard.subscriptionTrends')}</h2>
        <SubscriptionChart className="w-full" />
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{translate('dashboard.recentSubscriptions')}</h2>
          <Link to="/subscriptions" className="flex items-center text-sm text-primary hover:underline">
            {translate('dashboard.viewAllSubscriptions')}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        
        {isLoading ? (
          <Skeleton className="h-64 w-full rounded-lg" />
        ) : (
          <SubscriptionTable subscriptions={typedRecentSubscriptions} />
        )}
      </div>
    </MainLayout>
  );
};

export default Index;
